/* @jsxImportSource @emotion/react */
import { useState, useEffect, useRef } from 'react';
import useDimensions from 'react-cool-dimensions';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { animated, useTransition } from 'react-spring';

import createPersistedState from 'use-persisted-state';

import LetterAvatar from 'app/components/LetterAvatar';
import useKeyPress from 'app/hooks/useKeyPress';

import Search from 'app/components/nav/Search';
import Report from 'app/components/nav/Report';
import Delivery from 'app/components/nav/Delivery';
import Run from 'app/components/nav/Run';
import User from 'app/components/nav/User';
import Zone from 'app/components/nav/Zone';
import Self from 'app/components/nav/Self';
import Logo from 'app/components/nav/Logo';

import { css } from '@emotion/react';
import PropTypes from 'prop-types';

const componentCss = css`
    --siteMenuSpacing: 32px;
    height: 100vh;
    width: 64px;
    transition: height 0.25s ease-in-out 0s;
    left: 0px;
    position: relative;

    display: flex;
    justify-content: space-between;
    flex-direction: column;

    background-color: rgb(23, 43, 77);
    box-sizing: border-box;

    .SiteHeader {
        --siteMenuTransition: 500ms;
        --siteMenuArrowSpacing: 13px;
        --siteMenuArrowOffset: 0;
        position: relative;
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        background: none;

        &.IsVisible {
            .SiteHeaderMenuContainer {
                opacity: 1;
                z-index: 10;
            }

            .SiteHeaderArrow {
                transform: translateX(0) translateY(var(--siteMenuArrowOffset))
                    translateY(50%) rotate(-45deg);
            }
        }
    }

    .SiteHeaderMenuContainer {
        display: var(--desktopNavDisplay, block);
        top: calc(100% - 1px - var(--siteMenuArrowSpacing));
        overflow: hidden;
        opacity: 0;

        left: 60px;
        position: absolute;

        transition-property: opacity;
        transition: var(--siteMenuTransition);
    }

    .SiteHeaderNavItem {
        padding: 5px 20px;
        cursor: pointer;
        font-size: 1.3em;
        outline: 0;
    }

    .Card {
        position: relative;
        min-width: 100px;
        min-height: 72px;
        border-radius: var(--cardBorderRadius);
        background-color: #fff;
        box-shadow:
            0 50px 100px -20px rgba(50, 50, 93, 0.25),
            0 30px 60px -30px rgba(0, 0, 0, 0.3);
        overflow: hidden;
        height: 100%;
    }

    .SiteHeaderArrow {
        position: absolute;
        top: 8px;
        left: 10px;
        margin: 0 0 0 -6px;
        width: 16px;
        height: 16px;
        transform: translateY(var(--siteMenuArrowOffset)) translateY(50%)
            rotate(45deg);
        border-radius: 3px 0 0 0;
        background-color: #fff;
        box-shadow: -3px -3px 5px rgba(82, 95, 127, 0.04);
        transition-property: transform;
        transition-duration: var(--siteMenuTransition);
        z-index: 2;
    }

    .SiteMenu {
        --siteMenuOffset: 0;
        --siteMenuRotateX: 0;
        display: inline-block;
        padding-top: var(--siteMenuArrowSpacing);
        height: calc(var(--siteMenuHeight) + var(--siteMenuArrowSpacing));
        transform: translateX(10px) rotateY(var(--siteMenuRotateX));
        transform-origin: 50% -50px;
        transition: var(--siteMenuTransition);
        transition-property: transform, width, height;
        will-change: transform, width, height;
        position: relative;
    }

    .SiteMenu[hidden] {
        --siteMenuRotateX: 15deg;
    }

    .logo {
        margin-top: 24px;
        width: 36px;
        align-self: center;
    }

    .SiteHeaderNavList {
        display: var(--desktopNavDisplay, flex);
        align-items: center;
        margin: 0;
        padding: 0;
        list-style: none;
        flex-direction: column;

        position: absolute;
        height: 100vh;
        flex: 1;
        display: flex;
        flex-direction: column;
        align-self: center;
        justify-content: center;
        width: 60px;

        > li:not(:first-of-type):not(:last-of-type) {
            margin-top: 20px;
            margin-bottom: 20px;

            i {
                color: #fff;
            }
        }
    }
`;

const data = [
    {
        id: 1,
        style: {
            position: 'absolute',
            top: 0,
        },
        name: 'Client',
    },
    {
        id: 2,
        name: 'Search',
        child: <i className="fa-solid fa-search"></i>,
    },
    {
        id: 3,
        name: 'Reports',
        child: <i className="fa-solid fa-chart-bar"></i>,
    },
    {
        id: 4,
        name: 'Deliveries',
        child: <i className="fa-solid fa-box"></i>,
    },
    {
        id: 5,
        name: 'Runs',
        child: <i className="fa-solid fa-truck"></i>,
    },
    {
        id: 6,
        name: 'Users',
        child: <i className="fa-solid fa-users"></i>,
    },
    {
        id: 7,
        name: 'Zones',
        child: <i className="fa-solid fa-map"></i>,
    },
    {
        id: 8,
        style: {
            position: 'absolute',
            bottom: 0,
        },
        name: 'Account',
        childF: (auth) => {
            return (
                <LetterAvatar
                    className="letter-avatar"
                    name={auth.user.name}
                    size="36"
                />
            );
        },
    },
];

const KeyWatch = (props) => {
    const isEscape = useKeyPress('Escape');
    const isCtrl = useKeyPress('Control');
    const isSpace = useKeyPress(' ');

    useEffect(() => {
        if (isEscape) {
            props.setClearNav(true);
            props.setSelectedNav(0);
            props.setIsShowing(false);
        }
    }, [isEscape]);

    useEffect(() => {
        if (isCtrl && isSpace) {
            props.setSelectedNav(2);
        }
    }, [isCtrl, isSpace]);

    return null;
};
KeyWatch.displayName = 'KeyWatch';
KeyWatch.propTypes = {
    setClearNav: PropTypes.func.isRequired,
    setSelectedNav: PropTypes.func.isRequired,
    setIsShowing: PropTypes.bool.isRequired,
};

const SiteNavigation = (props) => {
    const useClientState = createPersistedState('selected-client');
    const [selectedClientId] = useClientState(-1);
    const clients = useSelector((state) => state.CLIENT_LIST);
    const selectedClient = clients.items?.find(
        (client) => client.id === selectedClientId
    );

    // console.log("SN", selectedClient);

    data[0].child = selectedClient ? (
        selectedClient.logoURI ? (
            <img className="logo" src={selectedClient.logoURI} />
        ) : (
            <LetterAvatar
                className="letter-avatar logo"
                name={selectedClient.name}
                size="36"
            />
        )
    ) : (
        <img className="logo" src="/assets/HDS-Icon-White.png" />
    );

    const auth = useSelector((state) => state.auth);

    const [selectedNav, setSelectedNav] = useState(0);
    const [clearNav, setClearNav] = useState(false);
    const [isShowing, setIsShowing] = useState(false);

    const [menuWidth, setMenuWidth] = useState('0px');
    const [menuHeight, setMenuHeight] = useState('0px');

    const menuDi = useDimensions({});

    const logoD = useDimensions({});
    const searchD = useDimensions({});
    const reportD = useDimensions({});
    const deliveryD = useDimensions({});
    const runD = useDimensions({});
    const userD = useDimensions({});
    const zoneD = useDimensions({});
    const selfD = useDimensions({});

    const timer = useRef(null);

    let currentD = null;

    if (selectedNav === 1) {
        currentD = logoD;
    } else if (selectedNav === 2) {
        currentD = searchD;
    } else if (selectedNav === 3) {
        currentD = reportD;
    } else if (selectedNav === 4) {
        currentD = deliveryD;
    } else if (selectedNav === 5) {
        currentD = runD;
    } else if (selectedNav === 6) {
        currentD = userD;
    } else if (selectedNav === 7) {
        currentD = zoneD;
    } else if (selectedNav === 8) {
        currentD = selfD;
    }

    useEffect(() => {
        if (selectedNav === 1) {
            setMenuWidth(logoD.width);
            setMenuHeight(logoD.height);
        } else if (selectedNav === 2) {
            setMenuWidth(searchD.width);
            setMenuHeight(searchD.height);
        } else if (selectedNav === 3) {
            setMenuWidth(reportD.width);
            setMenuHeight(reportD.height);
        } else if (selectedNav === 4) {
            setMenuWidth(deliveryD.width);
            setMenuHeight(deliveryD.height);
        } else if (selectedNav === 5) {
            setMenuWidth(runD.width);
            setMenuHeight(runD.height);
        } else if (selectedNav === 6) {
            setMenuWidth(userD.width);
            setMenuHeight(userD.height);
        } else if (selectedNav === 7) {
            setMenuWidth(zoneD.width);
            setMenuHeight(zoneD.height);
        } else if (selectedNav === 8) {
            setMenuWidth(selfD.width);
            setMenuHeight(selfD.height);
        }

        const timeout = setTimeout(() => {
            if (selectedNav > 0) {
                setIsShowing(true);
            }
        }, 100);

        return () => clearTimeout(timeout);
    }, [selectedNav, currentD]);

    useEffect(() => {
        setClearNav(true);
    }, [props.location.pathname]);

    // useEffect(() => {
    // 	setTimeout(() => {
    // 		setIsShowing(true);
    // 		setSelectedNav(8);
    // 	}, 100);
    // }, []);

    const alignX =
        selectedNav === 1
            ? 0
            : selectedNav === 8
              ? menuHeight - 30
              : menuHeight / 2;
    const topPx =
        menuDi?.entry?.target.children[selectedNav - 1]?.offsetTop -
        alignX +
        5 +
        'px';
    const arrowOffset = (selectedNav === 8 ? -5 : -5) + alignX + 'px';

    const menuHeightShadow =
        selectedNav === 8 ? menuHeight + 28 : menuHeight + 120;

    useEffect(() => {
        if (timer.current && clearNav === false) {
            clearTimeout(timer.current);
            return;
        }

        timer.current = setTimeout(() => {
            setIsShowing(false);
        }, 100);

        const timeout = setTimeout(() => {
            if (clearNav === true) {
                setSelectedNav(0);
            }
        }, 100);

        return () => {
            clearTimeout(timeout);
            clearTimeout(timer.current);
        };
    }, [clearNav, setIsShowing]);

    const transition = useTransition(data, {
        trail: 400 / data.length,
        from: { opacity: 0 },
        enter: { opacity: 1 },
        leave: { opacity: 0 },
        config: { duration: 1000 },
    });

    return (
        <aside css={componentCss}>
            <KeyWatch
                setClearNav={setClearNav}
                setSelectedNav={setSelectedNav}
                setIsShowing={setIsShowing}
            />

            <header
                className={'SiteHeader' + (isShowing ? ' IsVisible' : '')}
                style={{ '--siteMenuArrowOffset': arrowOffset }}
            >
                <nav
                    className="SiteHeaderNav"
                    onMouseLeave={() => {
                        setClearNav(true);
                    }}
                >
                    <ul ref={menuDi?.observe} className="SiteHeaderNavList">
                        {/* {data.map(item => <li key={"LI-" + item.id}
								className="SiteHeaderNavItem"
								style={{ ...item.style }}
								onMouseEnter={() => {
									console.log(item)
									setClearNav(false);
									setSelectedNav(item.id)
								}}
							>
								<button className="SiteHeaderNavItem">{item.childF ? item.childF(auth) : item.child}</button>
							</li>)} */}
                        {transition((style, item) => (
                            <animated.li
                                key={'LI-' + item.id}
                                className="SiteHeaderNavItem"
                                style={{ ...style, ...item.style }}
                                onMouseEnter={() => {
                                    setClearNav(false);
                                    setSelectedNav(item.id);
                                }}
                            >
                                <Link to="/" className="SiteHeaderNavItem">
                                    {item.childF
                                        ? item.childF(auth)
                                        : item.child}
                                </Link>
                            </animated.li>
                        ))}
                    </ul>
                </nav>
                <div
                    onMouseEnter={() => {
                        setClearNav(false);
                    }}
                    onMouseLeave={() => {
                        setClearNav(true);
                    }}
                    style={{
                        width: menuWidth + 80 + 'px',
                        height: menuHeightShadow + 'px',
                        top: topPx,
                    }}
                    className="SiteHeaderMenuContainer"
                >
                    <div className="SiteHeaderArrow" />
                    <div
                        className="SiteMenu"
                        style={{
                            width: menuWidth + 'px',
                            '--siteMenuHeight': menuHeight + 'px',
                        }}
                        hidden={!isShowing}
                    >
                        <div className="Card">
                            <Logo
                                ref={logoD?.observe}
                                isVisible={selectedNav === 1}
                                selectedNav={selectedNav}
                                hideNav={() => {
                                    setIsShowing(false);
                                    setSelectedNav(0);
                                }}
                            />
                            <Search
                                ref={searchD?.observe}
                                isVisible={selectedNav === 2}
                                selectedNav={selectedNav}
                                hideNav={() => {
                                    setIsShowing(false);
                                    setSelectedNav(0);
                                }}
                            />
                            <Report
                                index={3}
                                ref={reportD?.observe}
                                selectedNav={selectedNav}
                            />
                            <Delivery
                                index={4}
                                ref={deliveryD?.observe}
                                selectedNav={selectedNav}
                            />
                            <Run
                                index={5}
                                ref={runD?.observe}
                                selectedNav={selectedNav}
                                auth={auth}
                            />
                            <User
                                index={6}
                                ref={userD?.observe}
                                selectedNav={selectedNav}
                                auth={auth}
                            />
                            <Zone
                                index={7}
                                ref={zoneD?.observe}
                                selectedNav={selectedNav}
                            />
                            <Self
                                ref={selfD?.observe}
                                selectedNav={selectedNav}
                            />
                        </div>
                    </div>
                </div>
            </header>
        </aside>
    );
};

SiteNavigation.displayName = 'SiteNavigation';
SiteNavigation.propTypes = {
    location: PropTypes.shape({
        pathname: PropTypes.string,
    }),
};

export default SiteNavigation;
